<script lang="ts">
import Icon from "@/Components/Generics/Icon.vue";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {
        Icon,
    },
    props: {
        secondary: {
            type: Boolean,
            default: false,
        },
        identifier: {
            type: String,
            default: null,
        },
        title: {
            type: String,
            default: "No title",
        },
        circular: {
            type: Boolean,
            default: false,
        },
        simple: {
            type: Boolean,
            default: false,
        },
        danger: {
            type: Boolean,
            default: false,
        },
        uppercase: {
            type: Boolean,
            default: false,
        },
        searching: {
            type: Boolean,
            default: false,
        },
        omitIconWhenSM: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clickStop: {
            type: Boolean,
            default: true,
        },
        type: {
            type: String as PropType<"button" | "submit" | "reset">,
            default: undefined,
        },
        small: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["click"],
    data: () => ({}),
    computed: {},
    methods: {
        onClick(event: Event) {
            if (this.clickStop) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.disabled) return;

            this.$emit("click", event);
        },
    },
});
</script>

<template>
    <button
        :type="type"
        class="generic-button flex flex--valign-c flex--halign-c flex--gap-20"
        :class="{ primary: !secondary && !simple, secondary: secondary, circular, simple, danger, small, 'omit-icon-when-sm': omitIconWhenSM }"
        :disabled="disabled"
        @click="onClick">
        <Icon v-if="identifier" class="icon" :identifier="identifier" :searching="searching" />

        <Transition name="generic-fade" mode="out-in">
            <span
                :key="title"
                class="text"
                :class="{
                    'text--primary-b text--less-gap': simple,
                    'text--color-white text--shadow': !simple,
                    'text--uppercase': uppercase,
                    ' text--size-22 text--semi-bold': !small,
                    ' text--size-20 text--semi-bold': small,
                }">
                {{ title }}
            </span>
        </Transition>
    </button>
</template>

<style lang="scss" scoped>
.generic-button {
    padding-left: rem(30);
    padding-right: rem(30);
    border-radius: rem(8);
    box-shadow: 0 rem(3) 0 0 #00000028;
    height: rem(65);
    pointer-events: all;
    cursor: pointer;

    border: none;

    &:disabled {
        cursor: auto;
        opacity: 0.6;
        filter: grayscale(1);

        pointer-events: none;
        & * {
            pointer-events: none;
        }
    }

    &.omit-icon-when-sm {
        @include media-breakpoint-down(sm) {
            .icon {
                display: none;
                appearance: none;
            }
        }
    }

    &.circular {
        height: rem(40);
        border-radius: rem(20);
    }

    &.simple {
        transition: background-color 0.05s ease-in-out;
        background-image: none;
        background-color: $color-accent-light-a;
        &:hover {
            background-color: $color-accent-light-b;
        }
    }

    &.primary {
        background-image: $primary-gradient;
        &:hover {
            background-image: $primary-gradient-lighter;
        }
    }
    &.secondary {
        background-image: $secondary-gradient;
        &:hover {
            background-image: $secondary-gradient-darker;
        }
    }
    &.danger {
        background-image: $danger-gradient;
        &:hover {
            background-image: $danger-gradient-darker;
        }
    }
    &.small {
        height: rem(35);
        padding-left: rem(25);
        padding-right: rem(25);
    }

    .icon {
        margin-left: rem(-15);
        width: rem(50);
        height: rem(50);
    }
}

@include media-breakpoint-down(md) {
    .generic-button {
        padding-left: rem(20);
        padding-right: rem(20);
        height: rem(55);
        gap: rem(15);

        &.circular {
            height: rem(35);
        }

        .icon {
            margin-left: rem(-10);
            width: rem(40);
            height: rem(40);
        }
    }
}
@include media-breakpoint-down(sm) {
    .generic-button {
        gap: rem(10);
    }
}
</style>
